// client/src/lib/listingScoring.js
import { CRITERIA, HARD_LIMITS } from "./criteria.js";
import { computeScore, vetoReasons } from "./scoring.js";
import { formatEUR, formatArea } from "./localization.js";
import { distanceKm } from "./distance.js";

// Patch #23 (S7): align price ceiling met landBudget criterium (€1M).
// De €1.1M buffer leverde mismatches op tussen town-veto en listing-veto.
const LISTING_HARD = { priceMax: 1_000_000, areaMinM2: 10_000 };

export function scoreListing(l, towns, concelhoBySlug, weights, enforceVeto, retreatMode = false) {
  const vetoes = [];

  // ── Listing-level harde criteria ─────────────────────────────
  if (!l.price_eur) vetoes.push("Prijs ontbreekt");
  else if (l.price_eur > LISTING_HARD.priceMax) vetoes.push(`Prijs ${formatEUR(l.price_eur)} > €${LISTING_HARD.priceMax/1000}k`);
  if (!l.area_total_m2) vetoes.push("Grondoppervlak onbekend");
  else if (l.area_total_m2 < LISTING_HARD.areaMinM2) vetoes.push(`Grond ${formatArea(l.area_total_m2)} < 1 ha`);
  if (vetoes.length) return { score: 0, veto: vetoes, breakdown: [], townName: null };

  // ── Town-matching binnen het concelho ────────────────────────
  const matches = towns.filter(t => t.concelho === l.concelho);
  let town = matches.length === 1 ? matches[0] : null;
  if (!town && matches.length > 1 && l.lat && l.lon) {
    town = matches.reduce((best, t) => {
      const d = distanceKm([l.lat, l.lon], [t.coords[0], t.coords[1]]);
      return !best || d < distanceKm([l.lat, l.lon], [best.coords[0], best.coords[1]]) ? t : best;
    }, null);
  } else if (!town && matches.length > 0) town = matches[0];
  if (!town) return { score: 0, veto: [`Geen town-data voor concelho ${l.concelho || "?"}`], breakdown: [], townName: null };

  const concelho = concelhoBySlug[l.concelho];

  // ── Patch #23 S2: erfgenaam climate-vetoes van de town ───────
  // Listings in Caminha (1440 mm regen) erven het neerslag-veto;
  // dat hoorde altijd al zo te zijn maar werd hier nooit toegepast.
  const townVetoes = vetoReasons(town, concelho);
  if (townVetoes.length) vetoes.push(...townVetoes);

  // ── Patch #23 S1: concelho meegeven aan computeScore zodat
  //    dryness + sunshine derived-scores correct gecomputeerd worden
  const baseline = computeScore(town, weights, concelho);
  const breakdown = [{ label: `Basisscore (${town.name})`, delta: Math.round(baseline), kind: "base" }];
  let score = baseline;

  // ── Listing-specifieke bonussen/penalties ────────────────────
  if (l.area_total_m2) {
    const extraHa = Math.max(0, l.area_total_m2 / 10000 - 1);
    const bonus = Math.min(10, Math.round(extraHa * 1.5));
    if (bonus > 0) {
      breakdown.push({ label: `Extra grond (${formatArea(l.area_total_m2)})`, delta: +bonus, kind: "plus" });
      score += bonus;
    }
  }
  if (l.price_eur) {
    const ratio = l.price_eur / LISTING_HARD.priceMax;
    const h = Math.round((1 - ratio) * 8);
    if (h !== 0) {
      breakdown.push({ label: `Budget-headroom (${Math.round((1-ratio)*100)}% onder ceiling)`, delta: h, kind: h>0?"plus":"minus" });
      score += h;
    }
  }
  if (l.bedrooms != null) {
    let d = 0, lbl = "";
    if (l.bedrooms >= 4) { d = 4; lbl = `T${l.bedrooms} — ruim voor gezin`; }
    else if (l.bedrooms === 3) { d = 2; lbl = `T3 — geschikt voor gezin`; }
    else if (l.bedrooms === 2) { d = -2; lbl = `T2 — krap voor gezin van 5`; }
    else if (l.bedrooms <= 1) { d = -6; lbl = `T${l.bedrooms} — te klein`; }
    if (d !== 0) { breakdown.push({ label: lbl, delta: d, kind: d>0?"plus":"minus" }); score += d; }
  }
  if (l.area_built_m2) {
    if (l.area_built_m2 >= 100 && l.area_built_m2 <= 400) {
      breakdown.push({ label: `Bouw ${l.area_built_m2}m² — bruikbare basis`, delta: +2, kind: "plus" });
      score += 2;
    } else if (l.area_built_m2 < 50) {
      breakdown.push({ label: `Bouw maar ${l.area_built_m2}m² — veel bijbouwen`, delta: -2, kind: "minus" });
      score -= 2;
    }
  }
  if (concelho) {
    if (concelho.fire_risk_class >= 4) {
      breakdown.push({ label: `Brand klasse ${concelho.fire_risk_class}/5`, delta: -3, kind: "minus" }); score -= 3;
    }
    if (concelho.water_status === "extreme") {
      breakdown.push({ label: `Water extreem (droogte)`, delta: -5, kind: "minus" }); score -= 5;
    } else if (concelho.water_status === "severe") {
      breakdown.push({ label: `Water ernstig`, delta: -3, kind: "minus" }); score -= 3;
    }
    if (retreatMode && concelho.al_open === false) {
      breakdown.push({ label: `AL-quotum gesloten (retreat-mode)`, delta: -6, kind: "minus" }); score -= 6;
    }
    if (concelho.fit_score === "strong") {
      breakdown.push({ label: `Gecureerd: Sterke match`, delta: +4, kind: "plus" }); score += 4;
    } else if (concelho.fit_score === "caution") {
      breakdown.push({ label: `Gecureerd: Oppassen`, delta: -4, kind: "minus" }); score -= 4;
    }
  }

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    veto: vetoes,
    breakdown,
    townName: town.name,
  };
}

export function colorForListingScore(score, veto) {
  if (veto?.length) return "#7a2e23";
  if (score >= 75) return "#3d6e48";
  if (score >= 60) return "#5a8268";
  if (score >= 45) return "#d4a851";
  if (score >= 30) return "#b85c4d";
  return "#9a6058";
}
